import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDocumentConfigurationRequest,
  saveDocumentConfigurationRequest,
} from '../../../redux/JewellerDocument/jewellerDocumentSlice';

const DOCUMENT_FILE_TYPE_OPTIONS = ['PDF', 'png', 'jpeg'];

const emptyDocumentFile = () => ({
  fileName: '',
  fileCount: 1,
  fileType: [],
});

const mapDocumentFiles = (files) =>
  files.map((item) => ({
    fileName: String(item?.fileName || '').toUpperCase(),
    fileCount: item?.fileCount ?? 1,
    fileType: Array.isArray(item?.fileType) ? item.fileType : [],
  }));

const DocumentConfiguration = ({ canSave = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector(
    (state) => state.jewellerDocument?.configuration,
  );
  const configurationLoaded = useSelector(
    (state) => state.jewellerDocument?.configurationLoaded,
  );
  const configurationLoading = useSelector(
    (state) => state.jewellerDocument?.configurationLoading,
  );
  const isLoadingDocumentConfig = !configurationLoaded || configurationLoading;
  const isSavingDocumentConfig = useSelector(
    (state) => state.jewellerDocument?.saving,
  );
  const [documentFiles, setDocumentFiles] = useState([emptyDocumentFile()]);

  const fileTypeOptions = Array.from(
    new Set([
      ...DOCUMENT_FILE_TYPE_OPTIONS,
      ...documentFiles.flatMap((item) =>
        Array.isArray(item.fileType) ? item.fileType : [],
      ),
    ]),
  );

  const updateDocumentFile = (index, patch) => {
    setDocumentFiles((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    );
  };

  useEffect(() => {
    dispatch(getDocumentConfigurationRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!configurationLoaded) return;
    if (Array.isArray(configuration) && configuration.length) {
      setDocumentFiles(mapDocumentFiles(configuration));
    } else {
      setDocumentFiles([emptyDocumentFile()]);
    }
  }, [configurationLoaded, configuration]);

  const handleAddDocumentFile = () => {
    setDocumentFiles((prev) => [...prev, emptyDocumentFile()]);
  };

  const handleRemoveDocumentFile = (index) => {
    setDocumentFiles((prev) => {
      if (prev.length === 1) {
        return [emptyDocumentFile()];
      }
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const handleSaveDocumentConfiguration = async () => {
    const payloadFiles = documentFiles.map((item) => ({
      fileName: String(item.fileName || '').trim().toUpperCase(),
      fileCount: Number(item.fileCount),
      fileType: Array.isArray(item.fileType) ? item.fileType : [],
    }));

    if (payloadFiles.some((item) => !item.fileName)) {
      toast.error('File name is required');
      return;
    }

    const names = payloadFiles.map((item) => item.fileName);
    if (new Set(names).size !== names.length) {
      toast.error('File name must be unique');
      return;
    }

    if (
      payloadFiles.some(
        (item) => !Number.isInteger(item.fileCount) || item.fileCount < 1,
      )
    ) {
      toast.error('File count must be at least 1');
      return;
    }

    if (payloadFiles.some((item) => item.fileType.length === 0)) {
      toast.error('Select at least one file type');
      return;
    }

    dispatch(saveDocumentConfigurationRequest({ file: payloadFiles }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        width: '100%',
        border: '1px solid #e6e8ec',
        borderRadius: '12px',
        mt: 2,
        boxShadow: '0 8px 24px rgba(32, 33, 36, 0.06)',
      }}
    >
      <Typography fontWeight={700} fontSize={24} mb={1} fontFamily={'Aeonik'}>
        {t('configuration.documentConfiguration')}
      </Typography>
      <Typography mb={3} color="#7B8499">
        {t('configuration.documentConfigurationDesc')}
      </Typography>

      {isLoadingDocumentConfig ? (
        <Typography color="#7B8499">Loading document configuration...</Typography>
      ) : (
        <>
          {documentFiles.map((item, index) => (
            <Box
              key={`document-file-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: canSave ? '1fr 1fr 1fr auto' : '1fr 1fr 1fr',
                },
                gap: 2,
                mb: 2,
                alignItems: 'flex-start',
              }}
            >
              <TextField
                label={t('configuration.fileName')}
                size="small"
                fullWidth
                value={item.fileName}
                disabled={!canSave}
                onChange={(e) =>
                  updateDocumentFile(index, {
                    fileName: e.target.value.toUpperCase(),
                  })
                }
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText={t('configuration.fileNameUppercase')}
              />
              <TextField
                label={t('configuration.fileCount')}
                size="small"
                fullWidth
                type="number"
                disabled={!canSave}
                inputProps={{ min: 1, step: 1 }}
                value={item.fileCount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    updateDocumentFile(index, { fileCount: value });
                  }
                }}
                helperText=" "
              />
              <FormControl fullWidth size="small" disabled={!canSave}>
                <InputLabel>{t('configuration.fileType')}</InputLabel>
                <Select
                  multiple
                  label={t('configuration.fileType')}
                  value={item.fileType}
                  onChange={(e) =>
                    updateDocumentFile(index, {
                      fileType:
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value,
                    })
                  }
                  renderValue={(selected) =>
                    (Array.isArray(selected) ? selected : [])
                      .map((type) => String(type).toUpperCase())
                      .join(', ')
                  }
                  sx={{
                    '& .MuiSelect-select': {
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  {fileTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox
                        size="small"
                        checked={item.fileType.indexOf(type) > -1}
                        sx={{ p: 0.5, mr: 1, pointerEvents: 'none' }}
                      />
                      {String(type).toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.4, mx: 1.75, minHeight: 20, visibility: 'hidden' }}
                >
                  {t('configuration.fileNameUppercase')}
                </Typography>
              </FormControl>
              {canSave && (
                <Button
                  variant="contained"
                  onClick={() => handleRemoveDocumentFile(index)}
                  sx={{
                    height: 40,
                    px: 2.5,
                    bgcolor: '#D93025',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#B3261E', boxShadow: 'none' },
                  }}
                >
                  {t('configuration.remove')}
                </Button>
              )}
            </Box>
          ))}

          {canSave && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  height: 40,
                  px: 2.5,
                  fontWeight: 600,
                  borderColor: '#752ADA',
                  color: '#752ADA',
                }}
                onClick={handleAddDocumentFile}
              >
                {t('configuration.addFile')}
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#752ADA',
                  textTransform: 'none',
                  height: 40,
                  px: 2.5,
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#641fbe', boxShadow: 'none' },
                }}
                onClick={handleSaveDocumentConfiguration}
                disabled={isSavingDocumentConfig}
              >
                {isSavingDocumentConfig
                  ? t('settings.saving')
                  : t('configuration.save')}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default DocumentConfiguration;
